import { useState } from "react";
import "../css/Auth.css";

// Shared layout for Login, Register, Forgot Password and Reset Password.
// Left: navy brand panel (logo, headline, how the product works).
// Right: the page's form, passed in as children.
export default function AuthLayout({ title, subtitle, children, footer }) {
  return (
    <div className="au-page">
      <aside className="au-brand">
        <div className="au-brand-top">
          <span className="au-logo-tile">
            <img src="/logo192.png" alt="" width="34" height="34" />
          </span>
          <span className="au-brand-name">LoneWolf</span>
          <span className="au-brand-product">Resume Optimizer</span>
        </div>

        <div className="au-brand-body">
          <h1 className="au-headline">Tailor every resume to the job in front of you.</h1>

          <ol className="au-steps">
            <li>
              <span className="au-step-num">1</span>
              <span>Add the candidate's master resume</span>
            </li>
            <li>
              <span className="au-step-num">2</span>
              <span>Paste the client's job description</span>
            </li>
            <li>
              <span className="au-step-num">3</span>
              <span>Download a tailored, ATS-scored resume</span>
            </li>
          </ol>
        </div>

        {/* Ridgeline drawing: decorative nod to the "Peaks" in LoneWolf */}
        <svg
          className="au-ridges"
          viewBox="0 0 600 180"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path d="M0 150 L70 112 L120 132 L200 70 L260 110 L330 52 L400 104 L470 76 L540 118 L600 96" className="au-ridge au-ridge-gold" />
          <path d="M0 168 L90 130 L150 148 L230 100 L300 136 L370 88 L440 128 L520 102 L600 130" className="au-ridge" />
          <path d="M0 180 L110 150 L180 166 L260 128 L340 160 L420 122 L500 150 L600 140" className="au-ridge au-ridge-faint" />
        </svg>
      </aside>

      <main className="au-main">
        <div className="au-form-wrap">
          <h2 className="au-title">{title}</h2>
          {subtitle && <p className="au-subtitle">{subtitle}</p>}
          {children}
          {footer && <p className="au-footer">{footer}</p>}
        </div>
      </main>
    </div>
  );
}

// Password field with a show/hide button.
export function PasswordInput({ id, value, onChange, placeholder, autoComplete }) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="au-password">
      <input
        id={id}
        className="au-input"
        type={visible ? "text" : "password"}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required
      />
      <button
        type="button"
        className="au-eye"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? "Hide password" : "Show password"}
        aria-pressed={visible}
      >
        {visible ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  );
}

// Main action button with a spinner while the request is running.
export function SubmitButton({ loading, children, loadingText }) {
  return (
    <button type="submit" className="au-submit" disabled={loading}>
      {loading && <span className="au-spinner" aria-hidden="true" />}
      {loading ? loadingText : children}
    </button>
  );
}

// Coloured message box. tone = "error" | "success"
export function AuthAlert({ tone = "error", children }) {
  return (
    <div className={`au-alert au-alert-${tone}`} role={tone === "error" ? "alert" : "status"}>
      {tone === "success" ? <CheckIcon /> : <AlertIcon />}
      <span>{children}</span>
    </div>
  );
}

// --- Small inline icons (no icon library needed) ----------------------

const iconProps = {
  width: 18,
  height: 18,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
};

function EyeIcon() {
  return (
    <svg {...iconProps}>
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg {...iconProps}>
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 19c-7 0-11-7-11-7a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 7 11 7a18.5 18.5 0 0 1-2.16 3.19" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
      <path d="M1 1l22 22" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg {...iconProps}>
      <circle cx="12" cy="12" r="10" />
      <path d="m8 12 3 3 5-6" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg {...iconProps}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 7v6" />
      <path d="M12 16.5v.5" />
    </svg>
  );
}